import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';

const Sidebar = ({ menuItems, logo, isOpen, onClose, homePath, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutModal, setLogoutModal] = useState(false);

  const handleLogout = () => {
    setLogoutModal(false);
    if (onLogout) {
      onLogout();
      return;
    }
    localStorage.clear();
    navigate('/');
  };

  const closeModal = () => {
    setLogoutModal(false);
  };

  const openModal = () => {
    setLogoutModal(true);
  };

  // Get dashboard path based on user role
  const getDashboardPath = () => {
    try {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        const role = userData?.user_details?.role || userData?.role || localStorage.getItem('role');
        const roleNum = typeof role === 'string' ? parseInt(role, 10) : role;
        
        if (roleNum === 1) {
          return '/landlord/dashboard';
        } else if (roleNum === 2) {
          return '/tenant/properties';
        }
      }
    } catch (error) {
      console.error('Error getting dashboard path:', error);
    }
    // Default fallback
    return '/';
  };

  const dashboardPath = homePath || getDashboardPath();

  return (
    <>
      {/* Mobile overlay - closes sidebar when clicking outside */}
      {onClose && (
        <div
          className={`sidebar-overlay ${isOpen ? 'sidebar-overlay-open' : ''}`}
          aria-hidden="true"
          onClick={onClose}
        />
      )}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Mobile close button */}
        {onClose && (
          <div className="sidebar-mobile-header">
            <button
              type="button"
              className="sidebar-close-btn"
              aria-label="Close menu"
              onClick={onClose}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}
      <div className="sidebar-logo">
        {logo || (
          <Link to={dashboardPath} className="sidebar-logo-content" style={{ textDecoration: 'none', display: 'block' }}>
            <img src={window.getPublicUrl("/assets/images/Logo.png")} alt="ClearRent" className="sidebar-logo-img" />
          </Link>
        )}
      </div>
      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <span className="sidebar-section-title">Options</span>
          <ul className="sidebar-menu">
            {menuItems.map((item) => {
              // Check if current path matches exactly or starts with the menu item path
              // For settings/profile paths, also check if current path includes /settings
              // For properties and other parent routes, check if current path starts with the menu item path
              const isExactMatch = location.pathname === item.path;
              const isSettingsPath = item.path.includes('/settings');
              const isCurrentPathInSettings = location.pathname.includes('/settings');
              const isPropertiesPath = item.path.includes('/properties') && !item.path.includes('/settings');
              const isCurrentPathInProperties = location.pathname.includes('/properties') && !location.pathname.includes('/settings');
              // Active if exact match, or if it's a settings path and we're in settings, or if it's a properties path and we're in properties
              // A nested route (a detail page, e.g. /tenant/leases/:id) keeps its parent item lit.
              const isNestedRoute = location.pathname.startsWith(`${item.path}/`);
              const isActive = isExactMatch || isNestedRoute || (isSettingsPath && isCurrentPathInSettings) || (isPropertiesPath && isCurrentPathInProperties && location.pathname.startsWith(item.path));
              const isIconPath = typeof item.icon === 'string' && (item.icon.startsWith('/') || item.icon.includes('.svg') || item.icon.includes('.png') || item.icon.includes('.jpg'));
              
              return (
                <li key={item.path} className="sidebar-menu-item">
                  <Link
                    to={item.path}
                    className={`sidebar-menu-link ${isActive ? 'active' : ''}`}
                    onClick={onClose}
                  >
                    <span className="sidebar-menu-icon">
                      {isIconPath ? (
                        <img 
                          src={window.getPublicUrl ? window.getPublicUrl(item.icon) : item.icon} 
                          alt={item.label} 
                          className="sidebar-menu-icon-img"
                        />
                      ) : (
                        item.icon
                      )}
                    </span>
                    <span className="sidebar-menu-text">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
      <div className="sidebar-footer">
        <button onClick={openModal} className="sidebar-logout">
          <span className="sidebar-logout-icon">
            <img 
              src={window.getPublicUrl ? window.getPublicUrl("/assets/images/sidebarLogoutIcon.svg") : "/assets/images/sidebarLogoutIcon.svg"} 
              alt="Logout" 
              className="sidebar-logout-icon-img"
            />
          </span>
          <span className="sidebar-logout-text">Logout</span>
        </button>
      </div>

      <Modal
        isOpen={logoutModal}
        onRequestClose={closeModal}
        contentLabel="Logout Modal"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="logout-modal"
        overlayClassName="logout-modal-overlay"
      >
        <button className="logout-modal-close" onClick={closeModal}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="logout-modal-icon">
          <div className="logout-modal-icon-wrapper">
            <img 
              src={window.getPublicUrl ? window.getPublicUrl("/assets/images/sidebarLogoutIcon.svg") : "/assets/images/sidebarLogoutIcon.svg"} 
              alt="Logout" 
              className="logout-modal-icon-img"
            />
          </div>
        </div>
        <h2 className="logout-modal-title">Logout</h2>
        <p className="logout-modal-message">Are you sure you want to log out of your account?</p>
        <div className="logout-modal-buttons">
          <button className="logout-modal-cancel" onClick={closeModal}>Cancel</button>
          <button className="logout-modal-confirm" onClick={handleLogout}>Logout</button>
        </div>
      </Modal>
    </aside>
    </>
  );
};

export default Sidebar;
